import React from 'react'

import { XIcon } from 'lucide-react'
import { classList } from 'react-classlist-helper'

import { LocationType } from '~/client/graph/types.generated'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import SitemapItem from '~/client/src/shared/models/SitemapItem'
import { NOOP } from '~/client/src/shared/utils/noop'

import SitemapAttributeIconComponent from './SitemapAttributeIcon'

import Colors from '~/client/src/shared/theme.module.scss'
import './SitemapAttributeTag.scss'

interface IProps {
  dataObject?: LocationBase
  sitemapItem?: SitemapItem
  className?: string
  contentContainerClassName?: string
  shouldShowAsTag: boolean
  isBordered?: boolean
  isRemovable?: boolean
  onRemove?: (tagId: string) => void
  onClick?: (tagId: string) => void
  iconSize?: number
  shouldUseAlternativeIcon?: boolean
  shouldHideIcon?: boolean
  shouldUnderlineOnClick?: boolean
}

const SitemapAttributeTag: React.FC<IProps> = ({
  dataObject,
  sitemapItem,
  shouldShowAsTag,
  children,
  className,
  contentContainerClassName = '',
  isBordered,
  isRemovable,
  onRemove = NOOP,
  onClick,
  iconSize,
  shouldUseAlternativeIcon,
  shouldHideIcon,
  shouldUnderlineOnClick = true,
}) => {
  const dataSource = dataObject || sitemapItem
  const color = dataSource?.color || Colors.neutral0

  const tagStyles: React.CSSProperties = isBordered
    ? { border: `1px solid ${color}` }
    : {}

  const handleRemove = (tagId: string, e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    onRemove(tagId)
  }

  const handleClick = onClick
    ? (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        onClick(dataSource?.id)
      }
    : undefined

  return (
    <div
      // tw:isolate keeps the tag's internal layering — translucent background
      // under label and icon — from competing with the host's. Those two are flex
      // items, so their z-index applies despite being unpositioned, and without a
      // stacking context here they are promoted into whatever context the tag
      // lands in: in a table they beat a frozen column's own z-index and stayed
      // painted over it while the tag's background correctly slid underneath.
      className={classList({
        'hierarchy-delivery-attribute-tag px4 brada3 relative tw:isolate': true,
        pointer: !!onClick,
        [className || '']: !!className,
      })}
      style={tagStyles}
      onClick={handleClick}
    >
      {!!dataSource && !shouldHideIcon && (
        <div
          className={classList({
            'hierarchy-delivery-attribute-tag-icon no-grow row y-center pr4': true,
            'hierarchy-delivery-attribute-tag-icon-project':
              dataObject?.type === LocationType.ProjectLocation,
          })}
        >
          <SitemapAttributeIconComponent
            dataObject={dataObject}
            sitemapItem={sitemapItem}
            customProps={{ size: iconSize }}
            shouldUseAlternativeIcon={shouldUseAlternativeIcon}
          />
        </div>
      )}
      <div
        className={classList({
          'hierarchy-delivery-attribute-tag-content': true,
          'tw:underline': !!onClick && shouldUnderlineOnClick,
          [contentContainerClassName]: true,
        })}
      >
        {children}
      </div>
      {isRemovable && (
        <div
          className="row hierarchy-delivery-attribute-tag-remove-icon pointer"
          onClick={e => handleRemove(dataSource?.id, e)}
        >
          <XIcon size={16} />
        </div>
      )}
      {shouldShowAsTag && (
        <div className="tag-background" style={{ background: color }} />
      )}
    </div>
  )
}

export default SitemapAttributeTag
